/**
 * Shared Permission Form Component - Redesigned
 * Slide-over panel for creating/editing permissions with improved UX
 */

import { useState, useEffect, type JSX } from 'react'
import type { Permission, DialogMode, CreatePermissionInput, UpdatePermissionInput } from './types.js'

interface PermissionFormProps {
  visible: boolean
  mode: DialogMode
  permission: Permission | null
  loading: boolean
  onSave: (data: CreatePermissionInput | UpdatePermissionInput) => Promise<void>
  onCancel: () => void
}

const DOMAIN_SUGGESTIONS = [
  'platform', 'admin', 'users', 'roles', 'permissions',
  'tenants', 'billing', 'reports', 'audit', 'settings',
]

const ACTION_SUGGESTIONS = ['view', 'create', 'update', 'delete', 'manage', 'export', 'import', 'approve']

export function PermissionForm({
  visible,
  mode,
  permission,
  loading,
  onSave,
  onCancel,
}: PermissionFormProps): JSX.Element {
  const [formData, setFormData] = useState({
    key: '',
    description: '',
  })
  const [keyError, setKeyError] = useState('')

  useEffect(() => {
    if (!visible) return
    if (mode === 'edit' && permission) {
      setFormData({ key: permission.key, description: permission.description || '' })
      setKeyError('')
    } else if (mode === 'create') {
      setFormData({ key: '', description: '' })
      setKeyError('')
    } else if (mode === 'view' && permission) {
      setFormData({ key: permission.key, description: permission.description || '' })
    }
  }, [visible, mode, permission])

  const validateKey = (key: string) => {
    if (!key) { setKeyError('Key is required'); return false }
    if (!/^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*$/.test(key)) {
      setKeyError('Format must be domain:action (e.g. platform:roles_manage)')
      return false
    }
    setKeyError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === 'create' && !validateKey(formData.key)) return
    if (!formData.description.trim()) return
    await onSave({ key: formData.key, description: formData.description })
  }

  const isView = mode === 'view'
  const isEdit = mode === 'edit'
  const title = isView ? 'View Permission' : isEdit ? 'Edit Permission' : 'Create Permission'

  const [domain = '', action = ''] = formData.key.split(':')

  return (
    <>
      {visible && (
        <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={onCancel} />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-2xl z-[9999] flex flex-col transform transition-transform duration-300 ease-in-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <i className="pi pi-key text-white text-sm" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{title}</h2>
              {isEdit && permission && <p className="text-xs text-gray-400 font-mono">{permission.key}</p>}
            </div>
          </div>
          <button onClick={onCancel} className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all">
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* View mode: info cards */}
            {isView && permission && (
              <div className="space-y-4">
                <div className="p-4 bg-[#3454cf]/5 border border-[#3454cf]/10 rounded-sm">
                  <p className="text-xs font-semibold text-[#3454cf] uppercase tracking-wide mb-1">Permission Key</p>
                  <code className="text-sm font-mono text-gray-800">{permission.key}</code>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50/50 border border-gray-100 rounded-sm">
                    <p className="text-xs text-gray-400 mb-1">Domain</p>
                    <p className="text-sm font-semibold text-gray-800">{permission.key.split(':')[0] || '—'}</p>
                  </div>
                  <div className="p-3 bg-gray-50/50 border border-gray-100 rounded-sm">
                    <p className="text-xs text-gray-400 mb-1">Action</p>
                    <p className="text-sm font-semibold text-gray-800">{permission.key.split(':')[1] || '—'}</p>
                  </div>
                  <div className="p-3 bg-gray-50/50 border border-gray-100 rounded-sm">
                    <p className="text-xs text-gray-400 mb-1">Roles Using</p>
                    <p className="text-sm font-semibold text-gray-800">{permission.roleCount ?? 0}</p>
                  </div>
                  <div className="p-3 bg-gray-50/50 border border-gray-100 rounded-sm">
                    <p className="text-xs text-gray-400 mb-1">System</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-sm ${permission.isSystem ? 'bg-amber-50 text-amber-700' : 'bg-[#3454cf]/5 text-[#3454cf]'}`}>
                      {permission.isSystem ? 'System' : 'Custom'}
                    </span>
                  </div>
                </div>
                {permission.description && (
                  <div className="p-3 bg-gray-50/50 border border-gray-100 rounded-sm">
                    <p className="text-xs text-gray-400 mb-1">Description</p>
                    <p className="text-sm text-gray-700">{permission.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* Create/Edit mode */}
            {!isView && (
              <div className="space-y-5">
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-50">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Permission Identity</h3>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-semibold text-gray-700">Permission Key <span className="text-red-500">*</span></label>
                      {isEdit ? (
                        <div className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-[14px] font-mono text-gray-500">{formData.key}</div>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={formData.key}
                            onChange={(e) => { setFormData({ ...formData, key: e.target.value }); if (keyError) validateKey(e.target.value) }}
                            onBlur={() => validateKey(formData.key)}
                            placeholder="domain:action"
                            className={`w-full rounded-xl border px-4 py-2.5 text-[14px] font-mono text-gray-900 placeholder:text-gray-400 outline-none transition-all ${
                              keyError ? 'border-red-300 bg-red-50/30 focus:border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 bg-gray-50/50 focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10'
                            }`}
                          />
                          {keyError && <p className="text-xs text-red-500">{keyError}</p>}
                          <p className="text-xs text-gray-400">Format: <code>domain:action</code> — e.g. <code className="text-[#3454cf]">platform:roles_manage</code></p>
                          <div className="mt-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-500">Quick builder</p>
                            <div className="flex gap-2 flex-wrap">
                              {DOMAIN_SUGGESTIONS.map((d) => (
                                <button
                                  key={d}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, key: `${d}:${action || ''}` })}
                                  className={`text-xs px-2 py-1 rounded-sm border transition-all ${domain === d ? 'bg-[#3454cf]/5 border-[#3454cf]/30 text-[#3454cf] font-semibold' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-[#3454cf]/20'}`}
                                >
                                  {d}
                                </button>
                              ))}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              {ACTION_SUGGESTIONS.map((a) => (
                                <button
                                  key={a}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, key: `${domain || ''}:${a}` })}
                                  className={`text-xs px-2 py-1 rounded-sm border transition-all ${action === a ? 'bg-[#3454cf]/5 border-[#3454cf]/30 text-[#3454cf] font-semibold' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-[#3454cf]/20'}`}
                                >
                                  {a}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-semibold text-gray-700">Description <span className="text-red-500">*</span></label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        rows={4}
                        placeholder="Describe what this permission allows..."
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10 resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!isView && (
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-white shrink-0">
              <button type="button" onClick={onCancel} className="text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="text-sm font-semibold px-5 py-2.5 rounded-sm bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 text-white transition-all flex items-center gap-2">
                {loading && <i className="pi pi-spin pi-spinner text-xs" />}
                {isEdit ? 'Update Permission' : 'Create Permission'}
              </button>
            </div>
          )}
        </form>
      </div>
    </>
  )
}
